import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { normalizeError } from "lib/error.utils";
import { queryKeys } from "lib/query-keys";
import {
  createWebDownloadsFolder,
  deleteWebDownloadFile,
  deleteWebDownloadsFolder,
  listWebDownloadsDirectory,
  type WebDownloadsDirectoryListing,
  uploadWebDownloadFile,
} from "services/web-downloads/webDownloadsStorage";
import { EXPLORER_PATH_SEARCH_PARAM } from "../WebDownloadsManager.constants";
import { normalizeExplorerRelativePath, sanitizeFolderName } from "../WebDownloadsManager.utils";

export interface UseWebDownloadsExplorerReturn {
  currentPath: string;
  listing: WebDownloadsDirectoryListing | undefined;
  loading: boolean;
  error: Error | null;
  uploading: boolean;
  creatingFolder: boolean;
  deletingRelativePath: string | null;
  openFolder: (relativePath: string) => void;
  navigateToPath: (relativePath: string) => void;
  uploadFiles: (files: FileList | File[]) => Promise<void>;
  createFolder: (folderName: string) => Promise<string | null>;
  deleteFile: (relativePath: string) => Promise<void>;
  deleteFolder: (relativePath: string) => Promise<void>;
  refreshListing: () => Promise<void>;
}

export const webDownloadsQueryKeys = queryKeys.webDownloads;

export const useWebDownloadsExplorer = (): UseWebDownloadsExplorerReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const currentPath = useMemo(
    () => normalizeExplorerRelativePath(searchParams.get(EXPLORER_PATH_SEARCH_PARAM) ?? ""),
    [searchParams],
  );

  const {
    data: listing,
    isLoading,
    error: listingError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.webDownloads.directory(currentPath),
    queryFn: () => listWebDownloadsDirectory(currentPath),
  });

  const invalidateCurrentDirectory = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.webDownloads.directory(currentPath) });
  }, [currentPath, queryClient]);

  const changePath = useCallback(
    (relativePath: string): void => {
      const normalizedPath = normalizeExplorerRelativePath(relativePath);
      if (normalizedPath === currentPath) {
        return;
      }

      if (normalizedPath) {
        setSearchParams({ [EXPLORER_PATH_SEARCH_PARAM]: normalizedPath }, { replace: false });
        return;
      }

      setSearchParams({}, { replace: false });
    },
    [currentPath, setSearchParams],
  );

  const uploadMutation = useMutation({
    mutationFn: async ({ path, files }: { path: string; files: File[] }) => {
      for (const file of files) {
        await uploadWebDownloadFile(path, file);
      }
    },
    onSuccess: () => {
      void invalidateCurrentDirectory();
    },
  });

  const createFolderMutation = useMutation({
    mutationFn: ({ path, folderName }: { path: string; folderName: string }) =>
      createWebDownloadsFolder(path, folderName),
    onSuccess: () => {
      void invalidateCurrentDirectory();
    },
  });

  const deleteFileMutation = useMutation({
    mutationFn: (relativePath: string) => deleteWebDownloadFile(relativePath),
    onSuccess: () => {
      void invalidateCurrentDirectory();
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: (relativeFolderPath: string) => deleteWebDownloadsFolder(relativeFolderPath),
    onSuccess: () => {
      void invalidateCurrentDirectory();
    },
  });

  const uploadFiles = useCallback(
    async (files: FileList | File[]): Promise<void> => {
      const fileArray = Array.from(files);
      if (fileArray.length === 0) {
        return;
      }

      await uploadMutation.mutateAsync({ path: currentPath, files: fileArray });
    },
    [currentPath, uploadMutation],
  );

  const createFolder = useCallback(
    async (folderName: string): Promise<string | null> => {
      const sanitizedName = sanitizeFolderName(folderName);
      if (!sanitizedName) {
        return null;
      }

      await createFolderMutation.mutateAsync({ path: currentPath, folderName: sanitizedName });
      return sanitizedName;
    },
    [createFolderMutation, currentPath],
  );

  const deleteFile = useCallback(
    async (relativePath: string): Promise<void> => {
      await deleteFileMutation.mutateAsync(relativePath);
    },
    [deleteFileMutation],
  );

  const deleteFolder = useCallback(
    async (relativePath: string): Promise<void> => {
      await deleteFolderMutation.mutateAsync(relativePath);
    },
    [deleteFolderMutation],
  );

  const refreshListing = useCallback(async (): Promise<void> => {
    await refetch();
  }, [refetch]);

  const deletingRelativePath = deleteFileMutation.isPending
    ? (deleteFileMutation.variables ?? null)
    : deleteFolderMutation.isPending
      ? (deleteFolderMutation.variables ?? null)
      : null;

  const error = listingError ? normalizeError(listingError, "listing-failed") : null;

  return {
    currentPath,
    listing,
    loading: isLoading,
    error,
    uploading: uploadMutation.isPending,
    creatingFolder: createFolderMutation.isPending,
    deletingRelativePath,
    openFolder: changePath,
    navigateToPath: changePath,
    uploadFiles,
    createFolder,
    deleteFile,
    deleteFolder,
    refreshListing,
  };
};
